<template>
    <div
        v-if="commentsData.length !== 0"
        class="comments-section"
    >
        <template
            v-for="(comment, index) in commentsData"
            :key="comment.id"
        >
            <div class="comments-section__item-horizontal-list">
                <div class="comments-section__item">
                    <div class="comments-section__item-title">
                        {{ t('common.date') }}
                    </div>
                    <div class="comments-section__item-value">
                        {{ comment.date }}
                    </div>
                </div>
                <div class="comments-section__item--wide">
                    <div class="comments-section__item-title">
                        {{ t('common.comment') }}
                    </div>
                    <div
                        v-if="comment.comment.title"
                        class="comments-section__item-value comments-section__item-value--important"
                    >
                        {{ comment.comment.title }}
                    </div>
                    <div class="comments-section__item-value">
                        <div>
                            {{ comment.comment.text }}
                        </div>
                        <div v-if="comment.comment.attachment?.id" class="comments-section__item-attachment">
                            <Paperclip
                                class="comments-section__item-icon"/>
                            <span v-if="comment.comment.attachment.name">
                                {{ comment.comment.attachment.name }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="index !== commentsData.length - 1"
                class="comments-section__separator"
            />
        </template>
    </div>
    <EmptyStatePanelContent v-else />
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { useConstantsQuery } from '@/api/queries/general/useConstantsQuery'
import EmptyStatePanelContent from '@/components/states/EmptyStatePanelContent.vue'
import { AttachmentDto, GeneralCommentDto } from '@/api/generated/HCA'
import { Paperclip } from 'lucide-vue-next'
import { ActionType } from '@/configuration/actions/action-type'

type NormalizedCommentData = {
    id: number
    date: string
    comment: {
        text: string,
        attachment?: AttachmentDto,
        title?: string
    }
}

const props = defineProps<{
    data?: Array<GeneralCommentDto> | null
}>()

const { t } = useI18n()

const { data: constantsData } = useConstantsQuery()

const commentsData = computed<NormalizedCommentData[]>(() => {
    if (!props.data?.length || !constantsData.value) {
        return []
    }

    const normalizedComments: NormalizedCommentData[] = []

    props.data?.forEach((comment) => {
        const commentDate = format(new Date(comment.updatedAt ?? new Date()), DATE_FORMAT)

        normalizedComments.push({
            id: comment.id ?? -1,
            date: commentDate,
            comment: {
                text: comment.text ?? '-',
                attachment: comment.attachment,
                title: comment.actionType === ActionType.UnmarkedAsNoAction || comment.actionType === ActionType.MarkedAsNoAction
                    ? t(`dashboard.notifications.actions.${comment.actionType}`)
                    : undefined
            },
        })
    })

    return normalizedComments
})
</script>

<style scoped lang="scss">
.comments-section {
    display: flex;
    flex-direction: column;

    gap: $spacing-xs;

    &__item {
        break-inside: avoid;

        &--wide {
            grid-column: span 2 / span 6;
        }

        &-title {
            font-weight: 500;
            font-size: $default-font-size;
        }

        &-value {
            font-size: $default-font-size;

            &--important {
                font-weight: 500;
                margin-top: $spacing-3xs;
            }
        }

        &-attachment {
            display: flex;
            gap: $spacing-xs;
            align-items: center;
            color: $pbl-primary;
            margin-top: $spacing-2xs;

            &:hover {
                color: color-mix(in srgb, $pbl-primary 80%, black);
            }
        }

        &-icon {
            height: 14px;
            width: 14px;
        }
    }

    &__item-horizontal-list {
        break-inside: avoid;

        display: grid;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        gap: $spacing-l;
    }

    &__separator {
        height: 1px;
        width: 100%;
        background-color: $pbl-foreground-muted;
    }
}
</style>
